// Ui stuff
import { toast } from 'svelte-sonner';

// Phidgets
import * as phidget22 from '$lib/phidgets/phidget22.esm.min.js';

// RoastLog
import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { phidgetsRTDSettings } from '$lib/PersistedState.svelte';
import type { Nullable } from '$lib/types/RoastLog';
import type { ConnectOptions } from '..';
import { BaseDevice } from '../base';

// The TMP1200_0 RTD module is a single-channel VINT device. Unlike the
// thermocouple interface (a single multi-channel device where each probe is a
// separate `channel`), each RTD probe is its own TMP1200 plugged into its own
// VINT hub port. Probes are therefore distinguished by `hubPort` (0-3) and
// always read from `channel = 0`.
const RTD_CHANNEL = 0;

class PhidgetsRTDDevice extends BaseDevice {
  type: DeviceType = DeviceType.PHIDGETS_RTD;
  private phidgetSensors: Nullable<phidget22.TemperatureSensor>[] = [];

  protected async onConnect(options: ConnectOptions): Promise<boolean> {
    try {
      this.phidgetSensors = await this.phidgetsConnect(options);

      const allNull = this.phidgetSensors.every((sensor) => sensor === null);
      if (allNull) {
        toast.error(`Could not connect to Phidgets RTD. Please check that the
          Phidgets Network Server is running and that the RTD modules are
          plugged in to the VINT hub.`);
        return false;
      }

      return true;
    } catch (error) {
      logger.error(`Phidgets RTD connection error: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    this.phidgetSensors.forEach((sensor) => {
      if (sensor && sensor.isOpen) {
        logger.info(`Closing phidget RTD sensor on hub port ${sensor.hubPort}`);
        sensor.close();
      }
    });
  }

  protected onDetach = (p: phidget22.TemperatureSensor) => {
    if (p.isOpen) {
      logger.warn(`Closing phidget RTD sensor from detach on hub port ${p.hubPort}`);
      p.close();
    }
    const anythingOpen = this.phidgetSensors.map((s) => s?.isOpen).filter((s) => s === true);
    if (anythingOpen.length === 0) {
      toast.error(`Phidgets RTD disconnected. Shutting down.`);
      this.disconnect();
    }
  };

  protected async updateReadings(): Promise<void> {
    // No need to do anything here as the readings are updated via callbacks
    // from the Phidgets library
  }

  private async phidgetsConnect({
    tcSettings
  }: ConnectOptions): Promise<(phidget22.TemperatureSensor | null)[]> {
    try {
      const port = 8989;
      const conn = new phidget22.Connection(port, 'localhost');

      logger.info('Connecting to phidgets RTD...');
      await conn.connect();
      logger.info('Connected to phidgets RTD!');

      const enabledTcs = [true, tcSettings[1].active, tcSettings[2].active, tcSettings[3].active];

      // Device-level RTD configuration applied to every probe. The string
      // keys match the phidget22 enum member names exactly.
      const { rtdType, rtdWireSetup } = phidgetsRTDSettings.current;
      const phidgetRtdType = phidget22.RTDType[rtdType];
      const phidgetWireSetup = phidget22.RTDWireSetup[rtdWireSetup];

      // Wait for all sensors to be initialized
      const tempSensors = await Promise.all(
        enabledTcs.map(async (enabled, index) => {
          if (!enabled) {
            return null;
          }

          const tempSensor = new phidget22.TemperatureSensor();
          // Each RTD probe is a separate TMP1200 on its own hub port. The
          // probe index maps directly to the hub port, and the channel is
          // always 0 on a single-channel RTD module.
          tempSensor.hubPort = index;
          tempSensor.channel = RTD_CHANNEL;

          tempSensor.onTemperatureChange = (temperature: number) => {
            this.localReadings[index] = temperature;
          };

          tempSensor.onError = (e: number) => {
            if (e === 4105) {
              toast.error(
                `Phidgets RTD error. RTD ${index + 1} is enabled but an RTD module is not attached on hub port ${index}. Disable this channel in your settings.`
              );
              tempSensor.close();
            }
          };

          tempSensor.onDetach = (p: phidget22.TemperatureSensor) => {
            this.onDetach(p);
          };

          tempSensor.onPropertyChange = (p: any) => {
            logger.info(`Phidgets RTD Property Change: ${p}`);
          };

          tempSensor.onAttach = async () => {
            logger.info(
              `Phidgets RTD attached at hub port ${tempSensor.hubPort}, ${tempSensor.deviceID}`
            );
            await tempSensor.setDataInterval(500);
            await tempSensor.setRTDWireSetup(phidgetWireSetup);
            await tempSensor.setRTDType(phidgetRtdType);
          };

          try {
            await tempSensor.open(5000);
            return tempSensor;
          } catch (error) {
            logger.error(
              `Phidgets RTD connection error on hub port ${tempSensor.hubPort}: ${error}`
            );
            return null;
          }
        })
      );

      return tempSensors;
    } catch (error) {
      logger.error(`Phidgets RTD connection error: ${error}`);
      return Array(4).fill(null); // Return array of nulls if connection fails
    }
  }
}

export default PhidgetsRTDDevice;
